<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import PostSaleNotificationItem from './PostSaleNotificationItem.vue';
import { usePostSaleCaseNotifications } from '../composables/usePostSaleCaseNotifications';

const emit = defineEmits(['open']);
const { t } = useI18n();
const isOpen = ref(false);
const {
  filter,
  hasMore,
  load,
  loading,
  markRead,
  rows,
  selectFilter,
  unreadCount,
} = usePostSaleCaseNotifications({ notify: useAlert, t });

const tabs = computed(() => [
  { id: 'all', label: t('POST_SALE.NOTIFICATIONS.ALL') },
  { id: 'unread', label: t('POST_SALE.NOTIFICATIONS.UNREAD') },
]);
const recentLimit = computed(() => Date.now() - 5 * 24 * 60 * 60 * 1000);
const recent = computed(() =>
  rows.value.filter(
    item => new Date(item.created_at).getTime() >= recentLimit.value
  )
);
const previous = computed(() =>
  rows.value.filter(
    item => new Date(item.created_at).getTime() < recentLimit.value
  )
);
const badgeLabel = computed(() =>
  unreadCount.value > 99
    ? t('POST_SALE.NOTIFICATIONS.MAX_COUNT')
    : unreadCount.value
);

const openNotification = async notification => {
  await markRead(notification);
  emit('open', notification);
  isOpen.value = false;
};

onMounted(() => load());
</script>

<template>
  <div class="relative">
    <button
      type="button"
      class="relative grid size-9 place-items-center rounded-full border border-lais-border text-lais-text-muted transition hover:bg-lais-primary-50 hover:text-lais-primary"
      :aria-label="t('POST_SALE.NOTIFICATIONS.TITLE')"
      :aria-expanded="isOpen"
      data-testid="post-sale-notifications-button"
      @click="isOpen = !isOpen"
    >
      <span class="i-lucide-bell size-4" aria-hidden="true" />
      <span
        v-if="unreadCount"
        class="absolute -right-1 -top-1 min-w-4 rounded-full bg-red-500 px-1 text-center text-[10px] font-bold leading-4 text-white"
      >
        {{ badgeLabel }}
      </span>
    </button>

    <section
      v-if="isOpen"
      class="absolute right-0 top-11 z-30 w-[min(92vw,360px)] overflow-hidden rounded-lais border border-lais-border-soft bg-white shadow-xl"
      data-testid="post-sale-notifications-panel"
    >
      <div
        class="flex items-center justify-between border-b border-lais-border-soft px-4 py-3"
      >
        <h2 class="font-semibold text-lais-text">
          {{ t('POST_SALE.NOTIFICATIONS.TITLE') }}
        </h2>
        <span class="text-xs text-lais-text-muted">{{ unreadCount }}</span>
      </div>
      <div class="flex border-b border-lais-border-soft">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="flex-1 px-3 py-2 text-sm font-medium"
          :class="
            filter === tab.id
              ? 'border-b-2 border-lais-primary text-lais-primary'
              : 'text-lais-text-muted'
          "
          @click="selectFilter(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>
      <div class="max-h-[min(70vh,420px)] overflow-y-auto">
        <div v-if="loading && !rows.length" class="grid gap-2 p-3">
          <div
            v-for="item in 3"
            :key="item"
            class="h-14 animate-pulse rounded-lais-sm bg-lais-bg-soft"
          />
        </div>
        <p
          v-else-if="!rows.length"
          class="px-4 py-8 text-center text-sm text-lais-text-muted"
        >
          {{ t('POST_SALE.NOTIFICATIONS.EMPTY') }}
        </p>
        <template v-else>
          <p
            v-if="recent.length"
            class="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('POST_SALE.NOTIFICATIONS.RECENT') }}
          </p>
          <PostSaleNotificationItem
            v-for="notification in recent"
            :key="notification.id"
            :notification="notification"
            @open="openNotification"
          />
          <p
            v-if="previous.length"
            class="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            {{ t('POST_SALE.NOTIFICATIONS.PREVIOUS') }}
          </p>
          <PostSaleNotificationItem
            v-for="notification in previous"
            :key="notification.id"
            :notification="notification"
            @open="openNotification"
          />
          <button
            v-if="hasMore"
            type="button"
            class="w-full px-4 py-3 text-sm font-medium text-lais-primary"
            @click="load({ reset: false })"
          >
            {{ t('POST_SALE.NOTIFICATIONS.LOAD_MORE') }}
          </button>
          <p v-else class="px-4 py-3 text-center text-xs text-lais-text-muted">
            {{ t('POST_SALE.NOTIFICATIONS.END') }}
          </p>
        </template>
      </div>
    </section>
  </div>
</template>
